export type SortDirection = "asc" | "desc";

export interface TableSortState<TColumnKey extends string> {
  columnKey: TColumnKey;
  direction: SortDirection;
}

/**
 * A column's comparable value for one row. Returning `undefined` marks the cell as blank, and blank
 * cells always sort last regardless of direction.
 */
export type SortAccessor<TRow> = (row: TRow) => string | number | undefined;

/**
 * How a column turns into an ordering: either a single accessor, or a list of accessors applied in
 * order as tie-breaks (for example client name, then medical record number).
 */
export type ColumnSortAccessors<TRow> = SortAccessor<TRow> | SortAccessor<TRow>[];

/**
 * Decides whether an accessor's value counts as an empty cell. Tables that render a placeholder for
 * missing data (for example "—") pass a predicate that recognizes it, so those rows sort as blank
 * rather than alphabetically among real values.
 */
export type BlankValuePredicate = (value: string | number | undefined) => boolean;

function isBlankByDefault(value: string | number | undefined): boolean {
  return value === undefined || value === "";
}

function compareValues(left: string | number, right: string | number): number {
  if (typeof left === "number" && typeof right === "number") return left - right;
  return String(left).localeCompare(String(right), undefined, { sensitivity: "base" });
}

/**
 * Compares one accessor's values, keeping blanks last in both directions.
 *
 * The direction only flips the order of rows that have a value. An unknown charge or an undated
 * statement stays at the bottom ascending and descending alike, so reversing a column never fills
 * the first screen with rows that have nothing to compare.
 */
function compareWithBlanksLast(
  left: string | number | undefined,
  right: string | number | undefined,
  direction: SortDirection,
  isBlank: BlankValuePredicate,
): number {
  const leftBlank = isBlank(left);
  const rightBlank = isBlank(right);
  if (leftBlank && rightBlank) return 0;
  if (leftBlank) return 1;
  if (rightBlank) return -1;

  const result = compareValues(left as string | number, right as string | number);
  return direction === "asc" ? result : -result;
}

/**
 * Builds a sort function for a table from a map of column key to accessor(s).
 *
 * Direction flipping, blanks-last ordering, tie-break chaining, and copy-before-sort live here, so
 * a table only declares how each of its columns reads a value.
 *
 * @param accessors One entry per sortable column. An array value chains tie-breaks in order.
 * @param isBlank Optional override for what counts as an empty cell; defaults to `undefined` or "".
 * @returns A function taking rows and a sort state, returning a new sorted array. Passing no sort
 *   state returns the rows untouched.
 */
export function createTableSorter<TRow, TColumnKey extends string>(
  accessors: Record<TColumnKey, ColumnSortAccessors<TRow>>,
  isBlank: BlankValuePredicate = isBlankByDefault,
) {
  return function sortTableRows(
    rows: TRow[],
    sortState: TableSortState<TColumnKey> | undefined,
  ): TRow[] {
    if (!sortState) return rows;

    const columnAccessors = accessors[sortState.columnKey];
    const chain = Array.isArray(columnAccessors) ? columnAccessors : [columnAccessors];

    return [...rows].sort((left, right) => {
      for (const accessor of chain) {
        const result = compareWithBlanksLast(
          accessor(left),
          accessor(right),
          sortState.direction,
          isBlank,
        );
        if (result !== 0) return result;
      }
      return 0;
    });
  };
}

/**
 * Returns the next sort state after a column header click. The first click on a column sorts
 * ascending; clicking the same column again reverses direction.
 */
export function getNextTableSortState<TColumnKey extends string>(
  current: TableSortState<TColumnKey> | undefined,
  columnKey: TColumnKey,
): TableSortState<TColumnKey> {
  if (current?.columnKey === columnKey) {
    return { columnKey, direction: current.direction === "asc" ? "desc" : "asc" };
  }

  return { columnKey, direction: "asc" };
}
